import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Loader2, ExternalLink, Clock, AlertCircle, Tv, Film, Zap, History, Heart } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { searchFancaps, addWorkFavorite, removeWorkFavorite, getAllFavorites, checkFavoriteStatus, clearSearchCache } from '../api/fancaps'
import { useAuth } from '../contexts/AuthContext'

interface SearchResult {
  title: string
  url: string
  content_type: string
  year?: string
}

interface SearchHistory {
  query: string
  timestamp: number
  results_count: number
}

const SearchPage: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isAuthenticated, user } = useAuth()
  const [query, setQuery] = useState('')
  const [contentType, setContentType] = useState('all')
  const [searchHistory, setSearchHistory] = useState<SearchHistory[]>([])
  const [shouldSearch, setShouldSearch] = useState(false)
  const [manualLoading, setManualLoading] = useState(false)

  // 🔧 Bug修复：搜索参数标准化函数
  const normalizeSearchTerm = (term: string): string => {
    return term.toLowerCase().trim()
  }

  // 获取收藏数据 - 只有登录时才获取
  const { data: favoritesData } = useQuery({
    queryKey: ['favorites'],
    queryFn: getAllFavorites,
    staleTime: 30 * 1000,
    enabled: isAuthenticated, // 只有认证用户才获取收藏数据
  })

  // 处理作品收藏
  const handleWorkFavorite = async (result: SearchResult, isFavorited: boolean) => {
    // 首先检查用户是否已登录
    if (!isAuthenticated) {
      const shouldRedirect = confirm('请先登录后再进行收藏操作。是否前往登录页面？')
      if (shouldRedirect) {
        navigate('/login')
      }
      return
    }

    try {
      console.log('🔄 开始收藏操作:', { 
        title: result.title, 
        isFavorited, 
        contentType: result.content_type,
        isAuthenticated,
        user: user?.email 
      })
      
      if (isFavorited) {
        // 需要找到id来删除收藏
        const workFavorite = favoritesData?.data?.works?.find(w => w.title === result.title)
        if (workFavorite) {
          console.log('🗑️ 删除收藏:', workFavorite.id)
          await removeWorkFavorite(workFavorite.id)
        }
      } else {
        console.log('➕ 添加收藏:', { title: result.title, type: result.content_type, url: result.url })
        await addWorkFavorite(result.title, result.content_type, result.url)
      }
      
      console.log('✅ 收藏操作成功，刷新数据')
      // 刷新收藏数据
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
    } catch (error) {
      console.error('❌ 收藏操作失败:', error)
      
      // 提供更详细的错误信息
      let errorMessage = '收藏操作失败'
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        errorMessage = '请先登录后再进行收藏操作'
      } else if (error && typeof error === 'object' && 'detail' in error) {
        errorMessage = String(error.detail)
      }
      
      // 显示错误提示
      alert(errorMessage)
    }
  }

  // 检查作品是否已收藏
  const isWorkFavorited = (title: string): boolean => {
    return favoritesData?.data?.works?.some(work => work.title === title) || false
  }

  // 加载搜索历史
  useEffect(() => {
    const savedHistory = localStorage.getItem('fancaps-search-history')
    if (savedHistory) {
      try {
        setSearchHistory(JSON.parse(savedHistory))
      } catch (e) {
        console.error('加载搜索历史失败:', e)
      }
    }
  }, [])

  // 保存搜索历史
  const saveToHistory = useCallback((searchQuery: string, resultsCount: number) => {
    const newHistory = [
      { query: searchQuery, timestamp: Date.now(), results_count: resultsCount },
      ...searchHistory.filter(h => h.query !== searchQuery)
    ].slice(0, 10) // 只保留最近10次搜索

    setSearchHistory(newHistory)
    localStorage.setItem('fancaps-search-history', JSON.stringify(newHistory))
  }, [searchHistory])

  // 使用React Query进行搜索
  const {
    data: searchResponse,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['search', normalizeSearchTerm(query), contentType],
    queryFn: () => searchFancaps({ query: normalizeSearchTerm(query), content_type: contentType }),
    enabled: false, // 手动触发搜索
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5分钟内认为数据是新鲜的
  })

  // 监听搜索响应，保存历史记录
  useEffect(() => {
    if (searchResponse?.results && shouldSearch) {
      // 🔧 Bug修复：使用标准化的搜索词保存历史记录
      saveToHistory(normalizeSearchTerm(query), searchResponse.results.length)
      setShouldSearch(false) // 重置搜索状态
    }
  }, [searchResponse, shouldSearch, query, saveToHistory])

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim() || manualLoading) return
    
    setManualLoading(true)
    setShouldSearch(true)
    try {
      await refetch()
    } catch (error) {
      console.error('搜索失败:', error)
    } finally {
      setManualLoading(false)
      setShouldSearch(false)
    }
  }

  const handleHistoryClick = (historyQuery: string) => {
    setQuery(historyQuery)
    setShouldSearch(true)
  }

  const handleResultClick = (result: SearchResult) => {
    // 🎬 处理电影URL - 直接跳转到图片页面
    if (result.content_type === 'Movie') {
      // 🎯 修复：传递完整URL而不是提取ID
      console.log('🎬 检测到电影，传递完整URL:', result.url)
      navigate(`/images/${encodeURIComponent(result.url)}?title=${encodeURIComponent(result.title)}&workTitle=${encodeURIComponent(result.title)}&isMovie=true`)
      return
    }
    
    // 处理TV和动漫URL - 跳转到剧集列表页面
    const urlMatch = result.url.match(/showimages\.php\?(.+)$/)
    if (urlMatch) {
      const showId = urlMatch[1] // 例如: "26512-Gintama"
      // 🔧 修复：传递内容类型信息，解决TV剧集路径错误问题
      const contentType = result.content_type.toLowerCase() // TV -> tv, Anime -> anime
      // 🔧 Bug修复：使用标准化的搜索词传递给剧集页面
      navigate(`/episodes/${showId}?title=${encodeURIComponent(result.title)}&searchTerm=${encodeURIComponent(normalizeSearchTerm(query))}&contentType=${contentType}`)
    }
  }

  const getContentTypeIcon = (type: string): React.ReactElement => {
    switch (type) {
      case 'TV': return <Tv className="w-4 h-4" />
      case 'Movie': return <Film className="w-4 h-4" />
      case 'Anime': return <Zap className="w-4 h-4" />
      default: return <ExternalLink className="w-4 h-4" />
    }
  }

  const getContentTypeColor = (type: string) => {
    switch (type) {
      case 'TV': return 'bg-blue-100 text-blue-800'
      case 'Movie': return 'bg-green-100 text-green-800'
      case 'Anime': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // 防抖搜索建议
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const searchInputRef = useRef<HTMLDivElement>(null)
  
  useEffect(() => {
    if (query.length >= 2) {
      const timer = setTimeout(() => {
        // 基于搜索历史提供建议
        const filtered = searchHistory
          .filter(h => h.query.toLowerCase().includes(query.toLowerCase()))
          .map(h => h.query)
          .slice(0, 5)
        setSuggestions(filtered)
        setShowSuggestions(true)
      }, 300)
      return () => clearTimeout(timer)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }
  }, [query, searchHistory])

  // 点击外部区域隐藏建议框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // 处理输入框焦点事件
  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true)
    }
  }

  // 处理建议项点击
  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion)
    setShowSuggestions(false)
    setShouldSearch(true)
  }

  return (
    <div className="space-y-6">
      {/* 调试面板 - 只在开发环境显示 */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
          <h3 className="font-medium text-yellow-800 mb-2">调试信息 (开发环境)</h3>
          <div className="grid grid-cols-2 gap-4 text-yellow-700">
            <div>
              <strong>认证状态:</strong> {isAuthenticated ? '✅ 已登录' : '❌ 未登录'}
            </div>
            <div>
              <strong>用户信息:</strong> {user?.email || '无'}
            </div>
            <div>
              <strong>收藏数据:</strong> {favoritesData ? '✅ 已加载' : '❌ 未加载'}
            </div>
            <div>
              <strong>Token存在:</strong> {localStorage.getItem('fancap_auth_token') ? '✅ 有' : '❌ 无'}
            </div>
          </div>
        </div>
      )}
      
      {/* 页面标题 */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          搜索 Fancaps 内容
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          搜索您感兴趣的动漫、电影或TV剧，快速找到相关的图片资源
        </p>
      </div>

      {/* 搜索表单 */}
      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative" ref={searchInputRef}>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={handleInputFocus}
                placeholder="输入作品名称，如：Gintama, Clannad, Attack on Titan..."
                className="input text-lg pr-10"
                disabled={manualLoading}
              />
              {manualLoading && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 animate-spin text-gray-400" />
              )}
              
              {/* 搜索建议 */}
              {suggestions.length > 0 && showSuggestions && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg mt-1 shadow-lg z-10">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center text-sm"
                    >
                      <Clock className="w-4 h-4 mr-2 text-gray-400" />
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={manualLoading || !query.trim()}
              className="btn btn-primary px-6 py-3 text-lg inline-flex items-center"
            >
              {manualLoading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Search className="w-5 h-5 mr-2" />
              )}
              搜索
            </button>
          </div>

          {/* 内容类型选择 */}
          <div className="flex gap-2">
            <span className="text-sm text-gray-600 font-medium">类型:</span>
            {[
              { value: 'all', label: '全部' },
              { value: 'anime', label: '动漫' },
              { value: 'movies', label: '电影' },
              { value: 'tv', label: 'TV剧' }
            ].map((type) => (
              <label key={type.value} className="flex items-center">
                                 <input
                   type="radio"
                   name="contentType"
                   value={type.value}
                   checked={contentType === type.value}
                   onChange={(e) => setContentType(e.target.value)}
                   className="mr-1"
                   disabled={manualLoading}
                 />
                <span className="text-sm">{type.label}</span>
              </label>
            ))}
          </div>
        </form>

        {/* 搜索历史 */}
        {searchHistory.length > 0 && !shouldSearch && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <History className="w-4 h-4 mr-2" />
              最近搜索
            </h3>
            <div className="flex flex-wrap gap-2">
              {searchHistory.slice(0, 8).map((history, index) => (
                <button
                  key={index}
                  onClick={() => handleHistoryClick(history.query)}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-700 transition-colors"
                >
                  {history.query}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-800">搜索失败</h3>
                             <p className="text-sm text-red-700 mt-1">
                 {error && typeof error === 'object' && 'message' in error 
                   ? String((error as any).message) 
                   : '请检查网络连接并重试'}
               </p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-sm text-red-800 hover:text-red-900 underline"
              >
                重试搜索
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 搜索结果 */}
      {searchResponse?.results && (
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                搜索结果 ({searchResponse.total} 个)
              </h2>
              
              {/* 智能刷新按钮 */}
              <button 
                onClick={async (event) => {
                  console.log('🔄 智能刷新按钮被点击');
                  console.log('🔄 当前搜索参数:', { query, contentType });
                  console.log('🔄 当前结果数量:', searchResponse?.results?.length || 0);
                  console.log('🔄 缓存状态:', searchResponse?.cached ? '使用缓存' : '实时数据');
                  
                  // 添加视觉反馈
                  const button = event.currentTarget;
                  button.style.transform = 'rotate(180deg)';
                  button.style.backgroundColor = '#dbeafe';
                  button.disabled = true;
                  
                  try {
                    // 🎯 智能缓存清除：只清除当前搜索相关的缓存
                    const normalizedQuery = normalizeSearchTerm(query);
                    const cachePattern = `${normalizedQuery}:${contentType}`;
                    
                    console.log('🔄 清除后端搜索缓存...', cachePattern);
                    await clearSearchCache(cachePattern);
                    
                    // 清除前端React Query缓存
                    console.log('🔄 清除前端查询缓存...');
                    queryClient.invalidateQueries({ queryKey: ['search', normalizedQuery, contentType] });
                    
                    // 重新获取数据
                    console.log('🔄 开始重新获取数据...');
                    await refetch();
                    
                    console.log('🔄 智能刷新操作完成');
                  } catch (error) {
                    console.error('🔄 刷新失败:', error);
                    // 即使后端缓存清除失败，也尝试重新获取数据
                    await refetch();
                  } finally {
                    // 恢复按钮状态
                    setTimeout(() => {
                      button.style.transform = 'rotate(0deg)';
                      button.style.backgroundColor = '';
                      button.disabled = false;
                    }, 500);
                  }
                }}
                className="ml-3 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-all duration-300 disabled:opacity-50"
                title="智能刷新（清除相关缓存并重新获取数据）"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
            
            {searchResponse.cached && (
              <span className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                ⚡ 缓存数据
              </span>
            )}
          </div>

          {searchResponse.results.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">未找到相关内容</h3>
              <p className="text-gray-600">
                尝试使用不同的关键词或检查拼写是否正确
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {searchResponse.results.map((result: SearchResult, index: number) => {
                const isFavorited = isWorkFavorited(result.title)
                return (
                  <div
                    key={index}
                    className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300 hover:shadow-md transition-all group relative"
                  >
                    {/* 收藏按钮 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleWorkFavorite(result, isFavorited)
                      }}
                      className={`absolute top-3 right-3 z-10 p-1.5 rounded-full transition-all ${
                        !isAuthenticated
                          ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                          : isFavorited 
                            ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                            : 'text-gray-400 hover:text-red-500 bg-white hover:bg-red-50'
                      } shadow-sm border border-gray-200`}
                      title={!isAuthenticated ? '请先登录' : isFavorited ? '取消收藏' : '收藏作品'}
                      disabled={!isAuthenticated}
                    >
                      <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
                    </button>

                    <div 
                      onClick={() => handleResultClick(result)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-2 pr-10">
                        <h3 className="text-lg font-medium text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                          {result.title}
                        </h3>
                        <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-500 flex-shrink-0 ml-2" />
                      </div>
                  
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getContentTypeColor(result.content_type)}`}>
                          {getContentTypeIcon(result.content_type)}
                          <span className="ml-1">{result.content_type}</span>
                        </span>
                        
                        {result.year && (
                          <span className="text-sm text-gray-500">
                            {result.year}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchPage